import qs from 'qs'
import { parseDate, parseRate } from '@/domain/Parsers'
import { categorySort } from '@/domain/Sorters'
import { byPayment } from '@/domain/Filters'
import { cheapestToMostExpensiveRates } from '@/room-display/infrastructure/Factory'
import { origin } from '@/infrastructure/Configuration'
import { percentageDiscount } from '@/infrastructure/Filters'
import { paymentOptions } from '@/domain/FilterSelector'
import SearchTypes from '@/domain/SearchTypes'

export default {
  ratesByCategory: (state, getters) => {
    if (getters.isCorporateSearch) {
      return {
        pcr: state.rates.map(rate => parseRate(rate))
      }
    }

    const rates = state.rates.reduce((accum, rate) => {
      accum[rate.category] ? accum[rate.category].push(parseRate(rate)) : accum[rate.category] = [parseRate(rate)]
      return accum
    }, {})

    if (getters.getTierConditions) {
      rates.loyalty = [...ratesLoyaltyNotOnlyMember(rates.loyalty), ...ratesByTierDiscountFromCustomer(state.customer.tier, rates.standard)]
    }
    rates.standard = ratesWithoutTierDiscount(rates.standard)

    return rates
  },

  rateCategories: (_, getters) => Object.keys(getters.ratesByCategory).filter(rateCategory => getters.ratesByCategory[rateCategory].length).sort(categorySort),

  roomsByRateCategory: (state, getters) => category => {
    const cheapestToMostExpensiveRooms = (roomA, roomB) => {
      const ratesA = getters.ratesByRoomIdAndCategory(roomA, category)
      const ratesB = getters.ratesByRoomIdAndCategory(roomB, category)
      const cheapestRate = [...ratesA, ...ratesB].sort(cheapestToMostExpensiveRates)[0]
      return ratesA.includes(cheapestRate) ? -1 : 1
    }

    const rates = getters.ratesByCategory[category] && getters.ratesByCategory[category]
      .filter(byPayment(state.selectedPayment))
    return rates && state.rooms
      .filter(room => rates.some(rate => (rate.roomId === room.id || rate.roomId === room.tmsCode) && (rate.accessible || !state.onlyAccessible)))
      .sort(cheapestToMostExpensiveRooms)
  },

  lowestRate: (_, getters) => (category) => {
    const rates = getters.ratesByCategory[category] && getters.ratesByCategory[category].filter(rate => !rate.averagePricing.points)
    return rates ? rates.reduce(
      (minimumRate, rate) => rate.averagePricing.price ? (rate.averagePricing.price.amount < minimumRate.averagePricing.price.amount ? rate : minimumRate) : minimumRate,
      rates[0]) : null
  },

  ratesByRoomIdAndCategory: (state, getters) => (room, category) => {
    let rates = getters.ratesByCategory[category].filter(rate => rate.roomId === room.id || rate.roomId === room.tmsCode)

    rates = getters.isCorporateSearch ? rates : rates.filter(byPayment(state.selectedPayment))

    return rates.sort(cheapestToMostExpensiveRates)
  },

  lowestPriceForCategory: (_, getters) => category => {
    const prices = (getters.ratesByCategory[category] || [])
      .map((rate) => rate.averagePricing)
      .filter(averagePricing => averagePricing && !averagePricing.points)
      .map(pricing => pricing.price)

    const pricesWithPoints = (getters.ratesByCategory[category] || [])
      .map((rate) => rate.averagePricing)
      .filter(averagePricing => averagePricing.points && !averagePricing.price)
      .sort(function (a, b) {
        return a.points - b.points
      })
    const pricesWithCashAndPoints = (getters.ratesByCategory[category] || [])
      .map((rate) => rate.averagePricing)
      .filter(averagePricing => averagePricing.points && averagePricing.price)
    if (!prices.length && pricesWithPoints.length) {
      return pricesWithPoints.length > 0 ? pricesWithPoints[0] : null
    }
    if (!pricesWithPoints.length && prices.length) {
      return prices.length > 0 ? prices.reduce((minimumPrice, price) => price ? (price.amount < minimumPrice.amount ? price : minimumPrice) : minimumPrice,
        prices[0]) : null
    }
    return pricesWithCashAndPoints.length > 0 ? pricesWithCashAndPoints[0] : null
  },

  availabilityRequest: (state, getters) => {
    const request = {
      ...state.availabilityRequest,
      checkInDate: parseDate(state.availabilityRequest.checkInDate),
      checkOutDate: parseDate(state.availabilityRequest.checkOutDate),
      taxIncludedRegion: getters.taxIncludedRegion
    }

    if (getters.getTierConditions) {
      request.tierDiscount = {
        tierType: state.customer.tier
      }
    }

    return request
  },

  numberOfRooms: state => roomId => {
    const rooms = Math.min(...state.rates.filter(rate => rate.roomId === roomId && rate.numberOfRoomsLeft).map(rate => rate.numberOfRoomsLeft))
    return rooms === Infinity ? undefined : rooms
  },

  isLoading: state => state.loading > 0,

  isEcert: state => state.availabilityRequest.searchType === SearchTypes.ECERT,

  hasBookAndJoin: () => !!global.Cookie.read(global.cookieDictionary.bookAndJoin),

  selectedExchangeRate: (state, getters) => state.selectedCurrency.key === 'hotelCurrency' ? 1 : getters.exchangeRate(state.selectedCurrency.key),

  exchangeRate: state => currencyCode => {
    const exchangeRate = state.exchangeRates
      .find(exchangeRate => exchangeRate.currencyCode === currencyCode)
    return exchangeRate ? exchangeRate.exchangeRate : undefined
  },

  selectedCurrencyCode: state => state.selectedCurrency.key === 'hotelCurrency' ? '' : state.selectedCurrency.key,

  isSpecialRate: state => state.availabilityRequest.searchType !== SearchTypes.LOWEST,

  isCorporateSearch: state => state.availabilityRequest.searchType === SearchTypes.CORPORATE_ACCOUNT,

  areMatchingRatesAvailable: (state, getters) => (!getters.isSpecialRate && !getters.isEcert) || ((getters.isSpecialRate || getters.isEcert) && state.matchesRatesFound),

  currentCurrency: state => {
    const _hotelCurrency = () => {
      const rateWithPrices = state.rates.find(rate => !!rate.averagePricing && rate.averagePricing.price)
      if (rateWithPrices) {
        return rateWithPrices.averagePricing.price.currency
      }
    }
    return global.Cookie.read('preferredCurrency') || _hotelCurrency()
  },

  totalRatePoints: state => rate => {
    return rate.nights.reduce((accum, night) => accum + night.pricing.points, 0)
  },

  taxIncludedRegion: () => {
    const akamaiCookie = global.Cookie.read(global.cookieDictionary.akamai)
    return akamaiCookie && qs.parse(akamaiCookie).country === 'GB'
  },

  sharingUrl: ({ overviewPath }) => link => {
    const webExtraUrl = `${origin}${overviewPath}`
    return link
      .replace('%{url}', webExtraUrl)
      .replace('%{title}', webExtraUrl)
  },

  ratesHavePoints: state => {
    return state.rates.some(price => price.averagePricing.points)
  },

  bestDiscount: state => {
    const ratesWithDiscount = state.rates
      .filter(rate => rate.averagePricing)
      .filter(rate => rate.averagePricing.price)
      .filter(rate => rate.averagePricing.price.undiscountedAmount)

    return ratesWithDiscount.length ? ratesWithDiscount
      .map(rate => percentageDiscount(rate.averagePricing.price))
      .reduce((best, discount) => {
        return Number(best) > Number(discount) ? best : discount
      }) : null
  },

  ratesForCategory: state => category => state.rates.filter(rate => rate.category === category),

  currentCategoryHasPointsRates: (state, getters) => getters.ratesForCategory(state.currentCategory).some(rate => rate.averagePricing.points),

  getRoomsLeftForCurrentCategory: (state, getters) => state.currentCategory && getters.roomsByRateCategory(state.currentCategory)
    .map(room => getters.numberOfRooms(room.id))
    .filter(Number)
    .join('|'),

  paymentOptionsByCategory: state => state.currentCategory === 'loyalty' ? paymentOptions : paymentOptions.filter(e => e.key !== 'rdAll'),

  getTierConditions: state => !!(state.isTier && state.isLogged && state.availabilityRequest && state.availabilityRequest.searchType === SearchTypes.LOWEST && state.customer && state.customer.tier),

  getTravelAgencyCustomerId: state => state.travelAgencyCustomerId,

  getPromotionalCodeGroupId: state => state.promotionalCodeGroupId,

  lowestPriceForEachCategory: (state, getters) => {
    const rateCategory = getters.rateCategories
    const rateMapper = rateCategory.map((category) => (getters.ratesByCategory[category])
      .map((rate) => rate.averagePricing)
      .filter(averagePricing => averagePricing && !averagePricing.points)
      .map(pricing => pricing.price)
    )
    const lowestPricePerCategoryJson = {
      currency: state.selectedCurrency.key !== 'hotelCurrency' ? state.selectedCurrency.key : rateMapper[0].reduce((minimumPrice, price) => price ? (price.amount < minimumPrice.amount ? price : minimumPrice) : minimumPrice).currency,
      price: rateCategory.map((category, index) => {
        return {
          [category === 'loyalty' ? 'rewards' : category]: parseFloat(((rateMapper[index])
            .reduce((minimumPrice, price) => price ? (price.amount < minimumPrice.amount ? price : minimumPrice) : minimumPrice).amount * getters.selectedExchangeRate).toFixed(2))
        }
      })
    }

    return lowestPricePerCategoryJson
  },
  isLogged: state => state.isLogged,

  isNewCorporateProgramActive: state => !!state.customer && state.customer.programType === 'CORP',
  getQubitTracker: state => state.qubitTracker
}

function ratesByTierDiscountFromCustomer (tierCustomer, rates) {
  return rates ? rates.map(rate => rate.tierDiscount && rate.tierDiscount.tierType === tierCustomer ? rate : null).filter(Boolean) : []
}

function ratesWithoutTierDiscount (rates) {
  return rates ? rates.map(rate => rate.tierDiscount ? null : rate).filter(Boolean) : []
}

function ratesLoyaltyNotOnlyMember (rates) {
  return rates ? rates.map(rate => rate.memberOnly ? null : rate).filter(Boolean) : []
}
